/**
 * WorkStatusTable Component
 * 라이더 근무 현황 테이블 (협력사별 아코디언 형식, 컨테이너)
 */
import React, { useState, useCallback } from 'react';
import { SupplierWorkStatus, RiderWorkStatusItem } from '../types/riderWorkStatus.types';
import SupplierAccordion from "@/features/settlement/riderWorkStatus/components/table/SupplierAccordion";
import { useSortableTable } from "@/features/settlement/riderWorkStatus/hooks/useSortableTable";

interface WorkStatusTableProps {
    data: SupplierWorkStatus[];
    loading: boolean;
    minWorkDays?: number;
}

const WorkStatusTable: React.FC<WorkStatusTableProps> = ({ data, loading, minWorkDays }) => {
    const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
    const { sortKey, sortDirection, handleSort, sortRiders } = useSortableTable();

    /**
     * 협력사 아코디언 토글
     */
    const toggleSupplier = useCallback((suppId: string) => {
        setExpandedSuppliers((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(suppId)) {
                newSet.delete(suppId);
            } else {
                newSet.add(suppId);
            }
            return newSet;
        });
    }, []);

    /**
     * 전체 펼치기
     */
    const expandAll = () => {
        const allSuppIds = new Set(data.map((supplier) => supplier.suppId));
        setExpandedSuppliers(allSuppIds);
    };

    /**
     * 전체 접기
     */
    const collapseAll = () => {
        setExpandedSuppliers(new Set());
    };

    /**
     * 전체 펼침 여부 확인
     */
    const isAllExpanded = data.length > 0 && expandedSuppliers.size === data.length;

    /**
     * 라이더 목록 필터링 + 정렬
     */
    const getFilteredAndSortedRiders = useCallback(
        (riders: RiderWorkStatusItem[]): RiderWorkStatusItem[] => {
            // 1. 최소 근무일수 필터링
            let filtered = riders;
            if (minWorkDays !== undefined && minWorkDays > 0) {
                filtered = riders.filter((rider) => rider.totalWorkDays >= minWorkDays);
            }
            // 2. 정렬
            return sortRiders(filtered);
        },
        [minWorkDays, sortRiders],
    );

    if (loading && data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="empty-state">
                        <p>데이터를 불러오는 중...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="empty-state">
                        <p>조회된 근무 현황이 없습니다.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                {/* 전체 펼치기/접기 버튼 */}
                <div className="accordion-controls">
                    <button
                        className="accordion-toggle-btn"
                        onClick={isAllExpanded ? collapseAll : expandAll}
                    >
                        <i className={`fas fa-${isAllExpanded ? 'compress-alt' : 'expand-alt'}`}></i>
                        {isAllExpanded ? '전체 접기' : '전체 펼치기'}
                    </button>
                </div>
                <div className="work-status-accordion">
                    {data.map((supplier) => (
                        <SupplierAccordion
                            key={supplier.suppId}
                            supplier={supplier}
                            isExpanded={expandedSuppliers.has(supplier.suppId)}
                            onToggle={toggleSupplier}
                            getFilteredAndSortedRiders={getFilteredAndSortedRiders}
                            sortKey={sortKey}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default WorkStatusTable;
